import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { MapComponentProps } from 'types/mainPage';
import { MapUtils } from './MapUtils';
import {
  borderMaxZoom,
  defaultMapMaxZoom,
  defaultMapMinZoom,
  desktopExtent,
  desktopZoom,
  getBorderLayer,
  mobileExtent,
  mobileZoom,
  onlySubstrLayers,
  openTopoMap,
  osmLayer,
  scale_1_50_MapMaxZoom,
} from 'components/MapComponent/constants';
import MapContext from './MapContext';
import 'ol/ol.css';
import OlMap from 'ol/Map';
import OlView from 'ol/View';
import './styles.css';

export const MapComponent: React.FC<MapComponentProps> = ({
  children,
  zoom,
  center,
  toolbarOpen,
  headerHeight,
}): JSX.Element => {
  const [map, setMap] = useState<OlMap>();
  const mapElement = useRef<HTMLDivElement>(null);
  const { substrateLayer } = useAppSelector((state) => state.map);
  const { lang } = useAppSelector((state) => state.mainPage);
  const { currentLayer_scale_1_50, layer_scale_1_50_vector, currentLayer } =
    useAppSelector((state) => state.layers);
  const width = window.screen.width;

  const mapZoom = width && width < 768 ? mobileZoom : desktopZoom;
  const mapExtent = width && width < 768 ? mobileExtent : desktopExtent;
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!toolbarOpen && headerHeight) {
      const width = window.innerWidth;
      const height = window.innerHeight - headerHeight;
      map?.setSize([width, height]);
    } else {
      const height = window.innerHeight - Number(headerHeight);
      map?.setSize([width - 435, height]);
    }
  }, [toolbarOpen]);

  // on component mount
  useEffect(() => {
    let options = {
      view: new OlView({
        center,
        zoom: mapZoom,
        minZoom: defaultMapMinZoom,
        maxZoom: defaultMapMaxZoom,
        extent: mapExtent,
      }),
      layers: [openTopoMap, osmLayer],
      controls: [],
      overlays: [],
    };
    // @ts-ignore
    let mapObject = new OlMap(options);
    mapObject.setTarget(mapElement.current!);
    setMap(mapObject);

    return () => {
      mapObject.setTarget(undefined);
    };
  }, []);

  useEffect(() => {
    if (!map) return;
    const layers = map.getLayers().getArray();
    layers.forEach((lyr) => {
      const lyrName = lyr.get('name');

      if (onlySubstrLayers.includes(lyrName)) {
        lyr.setVisible(lyrName === substrateLayer);
      }
    });
  }, [substrateLayer, map]);

  useEffect(() => {
    if (!map) return;

    const borderLayer = getBorderLayer(lang);
    map.addLayer(borderLayer);

    const updateBorderVisibility = () => {
      const zoom = map.getView().getZoom();
      if (zoom !== undefined) {
        borderLayer.setVisible(zoom < borderMaxZoom);
      }
    };

    updateBorderVisibility();
    map.on('moveend', updateBorderVisibility);

    return () => {
      map.removeLayer(borderLayer);
      map.un('moveend', updateBorderVisibility);
    };
  }, [map, lang]);

  useEffect(() => {
    if (!map) return;

    if (currentLayer_scale_1_50) {
      map.getView().setMaxZoom(scale_1_50_MapMaxZoom);
    } else if (
      map.getView().getMaxZoom() !== defaultMapMaxZoom &&
      !layer_scale_1_50_vector
    ) {
      map.getView().setMaxZoom(defaultMapMaxZoom);
    }
  }, [map, currentLayer]);

  return (
    <MapContext.Provider value={{ map }}>
      <div
        id="map-ref"
        ref={mapElement}
        className="ol-map"
        style={{ width: '100%', height: '100%' }}
      >
        {children}
        <MapUtils />
      </div>
    </MapContext.Provider>
  );
};
